/**
 * Extract a human-readable error message from any backend error response.
 *
 * Handles all the shapes that FastAPI + custom middleware can throw at us:
 *   • Plain string                                  → returned as-is
 *   • { detail: 'string' }                          → use detail
 *   • { detail: { error, retry_after, ... } }       → friendly rate-limit msg
 *   • { detail: { message } }                       → use detail.message
 *   • { error, retry_after, window_seconds }        → friendly rate-limit msg
 *   • { error: 'string' }                           → use error
 *   • { message: 'string' }                         → use message
 *
 * This exists because FastAPI's `HTTPException(detail={...object...})` (used
 * by `rate_limiter.apply_rate_limiting`) serializes the dict directly under
 * the `detail` key. If callers naively do `setError(data.detail || ...)`
 * they end up putting an object in React state, which crashes rendering
 * with "Objects are not valid as a React child".
 *
 * @param {*} data — parsed JSON body (or Error) from a failed fetch
 * @param {string} fallback — message to use when no shape matches
 * @returns {string}
 */
export const extractErrorMessage = (data, fallback = 'Something went wrong') => {
  if (data == null) return fallback;
  if (typeof data === 'string') return data;

  // Synthetic / native Error instances
  if (data instanceof Error && data.message) return data.message;

  // Rate-limit / object-shaped detail
  const detail = data.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  if (detail && typeof detail === 'object') {
    if (typeof detail.retry_after === 'number') {
      const sec = detail.retry_after;
      const window = detail.window_seconds || sec;
      const niceWait = sec >= 60 ? `${Math.ceil(sec / 60)} minute${sec >= 120 ? 's' : ''}` : `${sec} second${sec === 1 ? '' : 's'}`;
      return `Too many attempts. Please wait ${niceWait} before trying again.${window ? '' : ''}`;
    }
    if (typeof detail.message === 'string' && detail.message.length > 0) return detail.message;
    if (typeof detail.error === 'string' && detail.error.length > 0) return detail.error;
  }

  // Flat shapes
  if (typeof data.retry_after === 'number') {
    const sec = data.retry_after;
    const niceWait = sec >= 60 ? `${Math.ceil(sec / 60)} minute${sec >= 120 ? 's' : ''}` : `${sec} second${sec === 1 ? '' : 's'}`;
    return `Too many attempts. Please wait ${niceWait} before trying again.`;
  }
  // Error-middleware envelope: { message: <generic>, details: { original_message } }
  // Prefer the specific original message over the generic wrapper text.
  if (data.details && typeof data.details.original_message === 'string' && data.details.original_message.length > 0) {
    return data.details.original_message;
  }
  if (typeof data.message === 'string' && data.message.length > 0) return data.message;
  if (typeof data.error === 'string' && data.error.length > 0) return data.error;

  return fallback;
};

export default extractErrorMessage;
